<template>
  <div class="announcements-widget">
    <div v-if="loading" class="text-center py-4">
      <v-progress-circular
        indeterminate
        size="24"
        color="primary"
      />
    </div>
    <div
      v-else-if="announcements.length === 0"
      class="text-center py-4"
    >
      <v-icon color="grey lighten-1" size="32">
        {{ icons.mdiBullhorn }}
      </v-icon>
      <p class="text-caption grey--text mt-2">
        No recent announcements
      </p>
    </div>
    <div v-else-if="visibleAnnouncements.length === 0" class="text-center py-4">
      <v-icon color="grey lighten-1" size="32">
        {{ icons.mdiBullhorn }}
      </v-icon>
      <p class="text-caption grey--text mt-2">
        All announcements dismissed
      </p>
      <v-btn x-small text color="primary" @click="resetDismissed">
        Show all ({{ dismissedIds.length }})
      </v-btn>
    </div>
    <div v-else class="announcements-scroll">
      <v-list
        dense
        class="pa-0"
        style="background: transparent;"
      >
        <v-list-item
          v-for="item in visibleAnnouncements.slice(0, 10)"
          :key="item._id"
          class="px-0 announcement-item"
          @click="openAnnouncement(item)"
          style="cursor: pointer;"
        >
          <v-list-item-icon class="mr-3 my-auto">
            <v-icon small color="primary">
              {{ icons.mdiInformation }}
            </v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title class="text-body-2">
              {{ truncate(item.body, 100) }}
            </v-list-item-title>
            <v-list-item-subtitle class="text-caption">
              {{ formatDate(item.createdAt) }}
            </v-list-item-subtitle>
          </v-list-item-content>
          <v-list-item-action class="my-auto">
            <v-btn
              icon
              x-small
              class="dismiss-btn"
              title="Dismiss this announcement"
              aria-label="Dismiss this announcement"
              @click.stop="dismissAnnouncement(item._id)"
            >
              <v-icon x-small color="grey lighten-1">
                {{ icons.mdiClose }}
              </v-icon>
            </v-btn>
          </v-list-item-action>
        </v-list-item>
      </v-list>
    </div>

    <NotificationDialog
      :notification="selectedAnnouncement"
      :notificationDialog="dialogOpen"
      @showNotificationDialog="dialogOpen = $event"
    />
  </div>
</template>

<script>
import {
  mdiBullhorn,
  mdiInformation,
  mdiClose,
} from '@mdi/js';
import { mapActions } from 'vuex';
import NotificationDialog from '../../common/announcement/NotificationDialog.vue';
import { toCentralDate } from '../../../util/shared/vue-global';

const STORAGE_KEY = 'dashboard_announcements_dismissed';

export default {
  name: 'AnnouncementsWidget',
  components: {
    NotificationDialog,
  },
  data: () => ({
    icons: { mdiBullhorn, mdiInformation, mdiClose },
    announcements: [],
    loading: true,
    dialogOpen: false,
    selectedAnnouncement: {
      _id: '',
      title: '',
      body: '',
      createdAt: '',
      attachments: [],
    },
    dismissedIds: (() => {
      try {
        const val = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
        return Array.isArray(val) ? val : [];
      } catch {
        return [];
      }
    })(),
  }),
  computed: {
    sortedAnnouncements() {
      return [...this.announcements].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
      );
    },
    visibleAnnouncements() {
      return this.sortedAnnouncements.filter(
        (a) => !this.dismissedIds.includes(a._id),
      );
    },
  },
  async created() {
    try {
      this.announcements = await this.loadAnnouncements(30);
      const validIds = new Set(this.announcements.map((a) => a._id));
      const trimmed = this.dismissedIds.filter((id) => validIds.has(id));
      if (trimmed.length !== this.dismissedIds.length) {
        this.dismissedIds = trimmed;
        try {
          if (trimmed.length > 0) {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
          } else {
            localStorage.removeItem(STORAGE_KEY);
          }
        } catch {
          // ignore storage errors
        }
      }
    } catch (e) {
      console.error('Failed to load announcements', e);
    } finally {
      this.loading = false;
    }
  },
  methods: {
    ...mapActions('notifications', ['loadAnnouncements']),
    truncate(text, len) {
      if (!text) return '';
      return text.length > len
        ? `${text.substring(0, len)}...`
        : text;
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      return toCentralDate(dateStr);
    },
    openAnnouncement(item) {
      this.selectedAnnouncement = {
        _id: item._id || '',
        title: item.title || 'Announcement',
        body: item.body || '',
        createdAt: item.createdAt || '',
        // Copied field by field, so anything missing here is dropped silently:
        // no error, the dialog simply shows no attachments.
        attachments: item.attachments || [],
      };
      this.dialogOpen = true;
    },
    dismissAnnouncement(id) {
      if (!id || this.dismissedIds.includes(id)) return;
      this.dismissedIds = [...this.dismissedIds, id];
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(this.dismissedIds));
      } catch {
        // storage full or unavailable — dismiss still works in-session
      }
    },
    resetDismissed() {
      this.dismissedIds = [];
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {
        // ignore storage errors
      }
    },
  },
};
</script>

<style scoped>
.announcement-item {
  border-bottom: 1px solid var(--widget-hairline, #f5f5f5);
  transition: background 0.15s ease;
}

.announcement-item:hover {
  background: var(--widget-hover-bg, rgba(0, 0, 0, 0.02));
}

.announcement-item:last-child {
  border-bottom: none;
}

.announcements-scroll {
  max-height: 320px;
  overflow-y: auto;
}

.announcements-scroll::-webkit-scrollbar {
  width: 4px;
}

.announcements-scroll::-webkit-scrollbar-thumb {
  background: var(--widget-scrollbar-thumb, #c8ddf8);
  border-radius: 4px;
}

.dismiss-btn {
  opacity: 0;
  transition: opacity 0.15s ease;
}

.announcement-item:hover .dismiss-btn,
.announcement-item:focus-within .dismiss-btn {
  opacity: 1;
}
</style>
